import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, InputLabel, IconButton, Tooltip, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import { useCart } from '../contexts/CartContext';
import { 
  Spa, 
  Face, 
  Wc, 
  ColorLens,
  BabyChangingStation,
  Search,
  Store,
  LocationOn,
  Close,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';

// Add keyframes for cursor blinking
const keyframes = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Inject the keyframes into the document
const injectGlobalStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.innerHTML = keyframes;
  document.head.appendChild(styleEl);
  return () => document.head.removeChild(styleEl);
};

// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const CosmeticCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchBarHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  overflow: 'hidden',
  zIndex: 1,
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
});

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

// Default logo component for cosmetic stores
const DefaultStoreLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#e3f2fd',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #bbdefb',
}));

// Cosmetic categories
const cosmeticProducts = [
  {
    category: { bangla: "ত্বকের যত্ন", english: "Skincare" },
    icon: <Spa />,
    items: [
      { bangla: "ফেসওয়াশ", english: "Face Wash", icon: <Face /> },
      { bangla: "ময়েশ্চারাইজার", english: "Moisturizer", icon: <Face /> },
      { bangla: "সানস্ক্রিন", english: "Sunscreen", icon: <Face /> },
      { bangla: "স্ক্রাব", english: "Scrub", icon: <Face /> },
      { bangla: "ফেস মাস্ক", english: "Face Mask", icon: <Face /> }
    ]
  },
  {
    category: { bangla: "মেকআপ", english: "Makeup" },
    icon: <ColorLens />,
    items: [
      { bangla: "ফাউন্ডেশন", english: "Foundation", icon: <ColorLens /> },
      { bangla: "লিপস্টিক", english: "Lipstick", icon: <ColorLens /> },
      { bangla: "আইলাইনার", english: "Eyeliner", icon: <ColorLens /> },
      { bangla: "মাস্কারা", english: "Mascara", icon: <ColorLens /> },
      { bangla: "কম্প্যাক্ট পাউডার", english: "Compact Powder", icon: <ColorLens /> }
    ]
  },
  {
    category: { bangla: "চুলের যত্ন", english: "Hair Care" },
    icon: <Wc />,
    items: [
      { bangla: "শ্যাম্পু", english: "Shampoo", icon: <Wc /> },
      { bangla: "কন্ডিশনার", english: "Conditioner", icon: <Wc /> },
      { bangla: "হেয়ার অয়েল", english: "Hair Oil", icon: <Wc /> },
      { bangla: "হেয়ার মাস্ক", english: "Hair Mask", icon: <Wc /> },
      { bangla: "সিরাম", english: "Serum", icon: <Wc /> }
    ]
  },
  {
    category: { bangla: "পুরুষদের প্রসাধনী", english: "Men's Grooming" },
    icon: <Face />,
    items: [
      { bangla: "আফটার শেভ", english: "After Shave", icon: <Face /> },
      { bangla: "বিয়ার্ড অয়েল", english: "Beard Oil", icon: <Face /> },
      { bangla: "ফেস ওয়াশ (পুরুষ)", english: "Men's Face Wash", icon: <Face /> },
      { bangla: "ডিওডোরেন্ট", english: "Deodorant", icon: <Face /> }
    ]
  },
  {
    category: { bangla: "বাচ্চাদের প্রসাধনী", english: "Baby Care Products" },
    icon: <BabyChangingStation />,
    items: [
      { bangla: "বেবি লোশন", english: "Baby Lotion", icon: <BabyChangingStation /> },
      { bangla: "বেবি পাউডার", english: "Baby Powder", icon: <BabyChangingStation /> },
      { bangla: "বেবি শ্যাম্পু", english: "Baby Shampoo", icon: <BabyChangingStation /> },
      { bangla: "বেবি ওয়াইপস", english: "Baby Wipes", icon: <BabyChangingStation /> }
    ]
  },
  {
    category: { bangla: "সুগন্ধি", english: "Fragrances" },
    icon: <Spa />,
    items: [
      { bangla: "পারফিউম", english: "Perfume", icon: <Spa /> },
      { bangla: "বডি স্প্রে", english: "Body Spray", icon: <Spa /> },
      { bangla: "ডিওডোরেন্ট", english: "Deodorant", icon: <Spa /> }
    ]
  },
  {
    category: { bangla: "নখের যত্ন", english: "Nail Care" },
    icon: <ColorLens />,
    items: [
      { bangla: "নেইল পলিশ", english: "Nail Polish", icon: <ColorLens /> },
      { bangla: "নেইল রিমুভার", english: "Nail Remover", icon: <ColorLens /> },
      { bangla: "নেইল ক্রীম", english: "Nail Cream", icon: <ColorLens /> }
    ]
  },
  {
    category: { bangla: "স্নান ও শরীরের যত্ন", english: "Bath & Body" },
    icon: <Wc />,
    items: [
      { bangla: "বডি ওয়াশ", english: "Body Wash", icon: <Wc /> },
      { bangla: "বডি লোশন", english: "Body Lotion", icon: <Wc /> },
      { bangla: "সাবান", english: "Soap", icon: <Wc /> },
      { bangla: "বডি স্ক্রাব", english: "Body Scrub", icon: <Wc /> }
    ]
  },
  {
    category: { bangla: "মুখের যত্ন", english: "Oral Care" },
    icon: <Spa />,
    items: [
      { bangla: "টুথপেস্ট", english: "Toothpaste", icon: <Spa /> },
      { bangla: "মাউথওয়াশ", english: "Mouthwash", icon: <Spa /> },
      { bangla: "টুথব্রাশ", english: "Toothbrush", icon: <Spa /> }
    ]
  },
  {
    category: { bangla: "সরঞ্জাম ও এক্সেসরিজ", english: "Tools & Accessories" },
    icon: <ColorLens />,
    items: [
      { bangla: "মেকআপ ব্রাশ", english: "Makeup Brushes", icon: <ColorLens /> },
      { bangla: "ব্লেন্ডার", english: "Beauty Blender", icon: <ColorLens /> },
      { bangla: "নেইল কাটার", english: "Nail Cutter", icon: <ColorLens /> },
      { bangla: "চুলের ব্রাশ", english: "Hair Brush", icon: <ColorLens /> }
    ]
  }
];

// List of stores
const stores = [
  {
    name: 'Beauty Hub',
    logo: '/images/logos/beautyhub.png',
    description: 'আধুনিক প্রসাধনীর সেরা ঠিকানা'
  },
  {
    name: 'Glamour Palace',
    logo: '/images/logos/glamour.png',
    description: 'প্রিমিয়াম প্রসাধন সামগ্রীর শোরুম'
  },
  {
    name: 'Cosmetics World',
    logo: '/images/logos/cosworld.png',
    description: 'সকল ধরনের প্রসাধনীর দোকান'
  },
  {
    name: 'Beauty Express',
    logo: '/images/logos/beautyexpress.png',
    description: 'দ্রুত ডেলিভারি সহ প্রসাধন সেবা'
  },
  {
    name: 'Makeup Studio',
    logo: '/images/logos/makeupsudio.png',
    description: 'পেশাদার মেকআপ সামগ্রীর দোকান'
  },
  {
    name: 'Natural Beauty',
    logo: '/images/logos/natural.png',
    description: 'প্রাকৃতিক উপাদানে তৈরি প্রসাধনী'
  }
];

// Sample cosmetics data
const cosmeticsData = [
  {
    id: 1,
    name: 'নিভিয়া সফট ক্রিম',
    englishName: 'Nivea Soft Cream',
    category: 'ময়েশ্চারাইজার',
    store: 'Beauty Hub',
    price: '৳৩৫০.০০',
    image: '/images/cosmetics/c1.jpg',
    description: 'ত্বকের জন্য মাল্টি-পারপাস ময়েশ্চারাইজিং ক্রিম',
    brand: 'Nivea'
  },
  {
    id: 2,
    name: 'লরিয়াল প্যারিস লিপস্টিক',
    englishName: "L'Oreal Paris Lipstick",
    category: 'লিপস্টিক',
    store: 'Glamour Palace',
    price: '৳৮৫০.০০',
    image: '/images/cosmetics/c2.jpg',
    description: 'লং-লাস্টিং ম্যাট ফিনিশ লিপস্টিক',
    brand: "L'Oreal"
  },
  {
    id: 3,
    name: 'পন্ডস এজ ডিফেন্স',
    englishName: "Pond's Age Defense",
    category: 'ফেস ক্রিম',
    store: 'Cosmetics World',
    price: '৳৪৫০.০০',
    image: '/images/cosmetics/c3.jpg',
    description: 'এন্টি-এজিং নাইট ক্রিম',
    brand: "Pond's"
  },
  {
    id: 4,
    name: 'মেবেলিন স্কাই হাই মাস্কারা',
    englishName: 'Maybelline Sky High Mascara',
    category: 'মাস্কারা',
    store: 'Beauty Express',
    price: '৳৯৫০.০০',
    image: '/images/cosmetics/c4.jpg',
    description: 'লম্বা ও ঘন পালক এফেক্টের মাস্কারা',
    brand: 'Maybelline'
  },
  {
    id: 5,
    name: 'গার্নিয়ার স্কিন নেচারালস',
    englishName: 'Garnier Skin Naturals',
    category: 'ফেসওয়াশ',
    store: 'Natural Beauty',
    price: '৳২৫০.০০',
    image: '/images/cosmetics/c5.jpg',
    description: 'প্রাকৃতিক উপাদানে সমৃদ্ধ ফেসওয়াশ',
    brand: 'Garnier'
  },
  {
    id: 6,
    name: 'লাকমে ৯ টু ৫ ফাউন্ডেশন',
    englishName: 'Lakme 9 to 5 Foundation',
    category: 'ফাউন্ডেশন',
    store: 'Makeup Studio',
    price: '৳৭৫০.০০',
    image: '/images/cosmetics/c6.jpg',
    description: 'লং-লাস্টিং ম্যাট ফিনিশ ফাউন্ডেশন',
    brand: 'Lakme'
  }
];

const bazars = [
  { bn: 'সকল বাজার', en: 'All Bazars' },
  { bn: 'চাচকৈড় বাজার', en: 'Chachkoir Bazar' },
  { bn: 'খুবজিপুর বাজার', en: 'Khubipur Bazar' },
  { bn: 'নাজিরপুর বাজার', en: 'Nazirpur Bazar' },
  { bn: 'বিলদোহর বাজার', en: 'Bildohar Bazar' },
  { bn: 'চাটখিল বাজার', en: 'Chatkhil Bazar' },
  { bn: 'সোনাইমুড়ি বাজার', en: 'Sonaimuri Bazar' },
  { bn: 'কেন্দ্রীয় বাজার', en: 'Central Bazar' },
  { bn: 'নিউ মার্কেট', en: 'New Market' },
  { bn: 'গুলিস্তান বাজার', en: 'Gulistan Bazar' },
  { bn: 'মৌচাক বাজার', en: 'Mouchak Bazar' },
  { bn: 'রকেট বাজার', en: 'Rocket Bazar' },
  { bn: 'কাওরান বাজার', en: 'Kawran Bazar' },
  { bn: 'ফার্মগেট বাজার', en: 'Farmgate Bazar' },
  { bn: 'মিরপুর বাজার', en: 'Mirpur Bazar' },
  { bn: 'উত্তরা বাজার', en: 'Uttara Bazar' },
  { bn: 'ধানমন্ডি বাজার', en: 'Dhanmondi Bazar' },
  { bn: 'মোহাম্মদপুর বাজার', en: 'Mohammadpur Bazar' },
  { bn: 'বনানী বাজার', en: 'Banani Bazar' },
  { bn: 'গুলশান বাজার', en: 'Gulshan Bazar' }
];

const CosmeticsBazar = () => {
  const { addToCart } = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const [typingText, setTypingText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingIndex, setTypingIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('সকল জেলা');
  const [selectedUpazilla, setSelectedUpazilla] = useState('সকল উপজেলা');
  const [selectedBazar, setSelectedBazar] = useState('সকল বাজার');
  const [districtSearchQuery, setDistrictSearchQuery] = useState('');
  const [upazillaSearchQuery, setUpazillaSearchQuery] = useState('');
  const [bazarSearchQuery, setBazarSearchQuery] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Typing animation effect
  const placeholderTexts = [
    'প্রসাধনীর নাম লিখুন...',
    'ব্র্যান্ডের নাম লিখুন...',
    'দোকানের নাম লিখুন...',
    'ক্যাটেগরির নাম লিখুন...',
    'প্রসাধনীর ধরন লিখুন...'
  ];

  // Inject keyframes for the blinking cursor animation
  useEffect(() => {
    const cleanup = injectGlobalStyles();
    return cleanup;
  }, []);

  // Typewriter effect for search placeholders
  useEffect(() => {
    const currentPlaceholder = placeholderTexts[placeholderIndex];
    
    const handleTyping = () => {
      if (isDeleting) {
        setTypingText(prev => prev.slice(0, -1));
        setTypingSpeed(50);
        
        if (typingText === '') {
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % placeholderTexts.length);
          setTypingSpeed(150);
        }
      } else {
        setTypingText(currentPlaceholder.slice(0, typingText.length + 1));
        setTypingSpeed(150);
        
        if (typingText === currentPlaceholder) {
          setTypingSpeed(2000);
          setIsDeleting(true);
        }
      }
    };
    
    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [typingText, isDeleting, placeholderIndex, placeholderTexts, typingSpeed]);

  const handleCategoryClick = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleSubCategoryClick = (mainCategoryKey, subCategory) => {
    setSelectedCategory(mainCategoryKey);
    setSelectedSubCategory(subCategory);
  };

  const handleStoreClick = (store) => {
    setSelectedStore(store.name);
  };

  const handleDistrictChange = (event) => {
    setSelectedDistrict(event.target.value);
  };

  const handleUpazillaChange = (event) => {
    setSelectedUpazilla(event.target.value);
  };

  const handleBazarChange = (event) => {
    setSelectedBazar(event.target.value);
  };

  const handleAddToCart = (product) => {
    console.log('CosmeticsBazar - Adding product to cart:', product);
    
    const cartProduct = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      store: product.store,
      stock: product.stock || 10
    };
    
    addToCart(cartProduct, 1);
  };

  // Filter bazars based on search query
  const filteredBazars = bazars.filter(
    bazar => bazar.bn.toLowerCase().includes(bazarSearchQuery.toLowerCase())
  );

  const filteredCosmetics = cosmeticsData.filter((cosmetic) => {
    const matchesCategory = !selectedCategory || 
      (selectedSubCategory ? cosmetic.category === selectedSubCategory : 
       cosmeticProducts.find(cat => cat.category.bangla === selectedCategory)?.items.some(item => item.bangla === cosmetic.category));
    const matchesStore = !selectedStore || cosmetic.store === selectedStore;
    const matchesSearch = !searchQuery || 
      cosmetic.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cosmetic.englishName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesStore && matchesSearch;
  });

  return (
    <>
      <Header />
      <Box sx={{ flexGrow: 1, p: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Search Header Bar */}
        <SearchBarHeader>
          <VideoBackground autoPlay muted loop>
            <source src="/videos/cosmetics.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
          <SearchOverlay />
          
          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              // Optional: Handle location change if needed
              console.log('Location changed:', location);
            }}
          />

          {/* Animated Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={typingText}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mx: 2, 
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              },
              zIndex: 2,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ 
                    opacity: isDeleting ? 0 : 1, 
                    transition: 'opacity 0.3s',
                    height: '1.2em', 
                    width: '0.15em', 
                    backgroundColor: 'primary.main',
                    display: 'inline-block',
                    animation: 'blink 1s step-end infinite'
                  }}/>
                </InputAdornment>
              )
            }}
          />
        </SearchBarHeader>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 } 
          }}>
            <Sidebar elevation={3}>
              <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <Spa sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    প্রসাধনীর ধরন
                  </Typography>
                </SectionHeader>
                <CategoryList sx={{ pl: 1, pr: 1 }}>
                  {cosmeticProducts.map((categoryData) => {
                    const categoryKey = categoryData.category.bangla;
                    return (
                      <React.Fragment key={categoryKey}>
                        <ListItem
                          button
                          onClick={() => handleCategoryClick(categoryKey)}
                          selected={selectedCategory === categoryKey}
                          sx={{ py: 1 }}
                        >
                          <ListItemIcon>
                            {categoryData.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={categoryData.category.bangla}
                            secondary={categoryData.category.english}
                            primaryTypographyProps={{ 
                              style: { 
                                fontWeight: selectedCategory === categoryKey ? 'bold' : 'normal',
                                fontSize: '0.9rem'
                              } 
                            }}
                            secondaryTypographyProps={{ 
                              style: { 
                                fontSize: '0.75rem',
                                color: 'text.secondary'
                              } 
                            }}
                          />
                          {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {categoryData.items.map((item) => (
                              <ListItem
                                button
                                key={`${categoryKey}-${item.bangla}`}
                                sx={{ pl: 4, py: 0.5 }}
                                selected={selectedCategory === categoryKey && selectedSubCategory === item.bangla}
                                onClick={() => handleSubCategoryClick(categoryKey, item.bangla)}
                              >
                                <ListItemIcon>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item.bangla}
                                  secondary={item.english}
                                  primaryTypographyProps={{ 
                                    style: { 
                                      fontSize: '0.8rem',
                                      fontWeight: selectedCategory === categoryKey && selectedSubCategory === item.bangla ? 'bold' : 'normal'
                                    } 
                                  }}
                                  secondaryTypographyProps={{ 
                                    style: { fontSize: '0.7rem' } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </CategoryList>

                <SectionHeader sx={{ mt: 2 }}>
                  <Store sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    দোকানের তালিকা
                  </Typography>
                </SectionHeader>
                <StoreList sx={{ pl: 1, pr: 1 }}>
                  {stores.map((store) => (
                    <ListItem
                      button
                      key={store.name}
                      selected={selectedStore === store.name}
                      onClick={() => handleStoreClick(store)}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <DefaultStoreLogo>
                          <Store sx={{ fontSize: 12, color: '#1976d2' }} />
                        </DefaultStoreLogo>
                      </ListItemIcon>
                      <ListItemText 
                        primary={store.name}
                        secondary={store.description}
                        primaryTypographyProps={{ 
                          fontSize: '0.875rem',
                          fontWeight: selectedStore === store.name ? 'bold' : 'normal' 
                        }}
                        secondaryTypographyProps={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </StoreList>
              </Box>
            </Sidebar>
          </Box>

          {/* Main Content */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Cosmetics Grid */}
              {filteredCosmetics.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(5, 1fr)', // Laptop: 5 cards per row
                      lg: 'repeat(6, 1fr)', // Big screen: 6 cards per row
                      '@media (min-width: 1980px)': {
                        gridTemplateColumns: 'repeat(9, 1fr)' // Extra large screens: 9 cards per row
                      }
                    },
                    width: '100%',
                  }}
                >
                  {filteredCosmetics.map((product) => (
                    <Box key={product.id} sx={{ display: 'flex', width: '100%' }}>
                      <CosmeticCard sx={{ width: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="160"
                            image={product.image}
                            alt={product.name}
                            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5', pt: 1 }}
                            onError={(e) => {
                              e.target.src = '/images/placeholder.png';
                            }}
                          />
                          <StyledBadge>{product.category}</StyledBadge>
                        </Box>
                        <CardContent sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          pb: '16px !important' // Override default padding bottom
                        }}>
                          <Box>
                            <Typography gutterBottom variant="h6" component="div">
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {product.englishName}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.primary">
                                <strong>ব্র্যান্ড:</strong> {product.brand}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>দোকান:</strong> {product.store}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{
                            mt: 'auto', // Push this box to the bottom
                            pt: 2 // Add some spacing from the content above
                          }}>
                            <Typography variant="h6" color="primary" sx={{ mb: 1, textAlign: 'center' }}>
                              {product.price}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1
                            }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                fullWidth
                              >
                                কিনুন
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                                onClick={() => handleAddToCart(product)}
                              >
                                ব্যাগে নিন
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </CosmeticCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                  <Spa sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    কোন প্রসাধনী পাওয়া যায়নি
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    অনুগ্রহ করে আপনার ফিল্টার বা সার্চ পরিবর্তন করুন
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedSubCategory('');
                      setSelectedStore('');
                      setSearchQuery('');
                    }}
                  >
                    সব ফিল্টার মুছুন
                  </Button>
                </Paper>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: "50px" }}>
        <Footer />
      </Box>
    </>
  );
};

export default CosmeticsBazar;
